import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import styled, { css } from 'styled-components';
import { FormGroup, ControlGroup, InputGroup, TextArea } from '@blueprintjs/core';
import { Button, ButtonGroup, Dialog, Icon, FileInput, MenuItem } from '@blueprintjs/core';
import { HTMLSelect } from '@blueprintjs/core';
import { Popover2, Tooltip2 } from '@blueprintjs/popover2';
import { Select } from '@blueprintjs/select';
import { Alignment, Classes, IconSize, DrawerSize, Position, Colors, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Pannellum } from '$components/pannellum-react-local';
import { useAuth } from '$root/contexts/auth';


export const EditTagMode = {
    ADD: 0,
    EDIT: 1,
}


export default function EditTagDialog(props)
{
    const {
        isOpen, projectId, documentId, tagId,
        mode, yaw, pitch, orientation, onClose
    } = props
    const { getProtectedResource, postFormDataProtected } = useAuth();

    const dropTarget = useRef();
    const dropDisplay = useRef();

    const [ isWorking, setIsWorking ] = useState(false);
    const [ tagSettings, setTagSettings ] = useState({
        categories: [],
        disciplines: [],
        statuses: [],
    });
    const [ tagData, setTagData ] = useState({
        title:'',
        tagId:'',
        category:-1,
        discipline:-1,
        status:-1,
        comment:'',
        photo:null,
    });


    const onAddTag = useCallback(() => {

        setIsWorking(true);

        const data = new FormData();

        data.append("documentId", documentId);
        data.append("title", tagData.title);
        data.append("comment", tagData.comment);
        data.append("category", tagData.category);
        data.append("discipline", tagData.discipline);
        data.append("status", tagData.status);
        data.append("yaw", yaw);
        data.append("pitch", pitch);
        data.append("orientation", orientation);

        if (tagData.photo)
            data.append("photo", tagData.photo);

        postFormDataProtected(`/tag2.php?task=tag-add`, data)
            .then(res => {
                console.log(res)

                onClose(true);
            })
            .catch(err => {
                console.error(JSON.stringify(err));

                onClose(false);
            })
        ;

    }, [tagData, setIsWorking]);


    const onEditTag = useCallback(() => {

        setIsWorking(true);

        const data = new FormData();

        data.append("documentId", documentId);
        data.append("id", tagId);
        data.append("comment", tagData.comment);
        data.append("status", tagData.status);
        data.append("title", tagData.title);
        data.append("category", tagData.category);
        data.append("discipline", tagData.discipline);

        if (tagData.photo)
            data.append("photo", tagData.photo);

        postFormDataProtected(`/tag2.php?task=tag-edit`, data)
            .then(res => {
                console.log(res)

                onClose(true);
            })
            .catch(err => {
                console.error(JSON.stringify(err));

                onClose(false);
            })
        ;

    }, [tagData, setIsWorking]);


    const handleDrop = useCallback((file) => {
        const reader = new FileReader();
        reader.addEventListener('load', ev => {
            dropDisplay.current.style.backgroundImage = `url("${ev.target.result}")`;

            setTagData(old => ({
                ...old,
                photo:file
            }));
        });
        reader.readAsDataURL(file);
    }, [setTagData]);


    useEffect(() => {

        if (!dropTarget.current)
            return ;

        const onDragEnter = e => {
            e.preventDefault();
            dropTarget.current.style.backgroundColor = 'rgba(255, 255, 255, 0.66)';
        };

        const onDragLeave = e => {
            e.preventDefault();
            dropTarget.current.style.backgroundColor = 'transparent';
        };

        const onDragOver = e => {
            e.preventDefault();
            dropTarget.current.style.backgroundColor = 'rgba(255, 255, 255, 0.66)';
        };

        const onDrop = e => {
            e.preventDefault();
            dropTarget.current.style.backgroundColor = 'transparent';

            if (e.dataTransfer.items &&
                e.dataTransfer.items.length === 1)
            {
                const t = e.dataTransfer.items[0];
                if (t.kind === 'file' &&
                    (t.type === 'image/png' || t.type === 'image/jpeg' || t.type === 'image/jpg'))
                {
                    handleDrop(e.dataTransfer.files[0]);
                }
            }
        };

        dropTarget.current.addEventListener('dragenter', onDragEnter);
        dropTarget.current.addEventListener('dragleave', onDragLeave);
        dropTarget.current.addEventListener('dragover', onDragOver);
        dropTarget.current.addEventListener('drop', onDrop);

        return _ => {
            if (dropTarget.current) {
                dropTarget.current.removeEventListener('dragenter', onDragEnter);
                dropTarget.current.removeEventListener('dragleave', onDragLeave);
                dropTarget.current.removeEventListener('dragover', onDragOver);
                dropTarget.current.removeEventListener('drop', onDrop);
            }
        };
    }, [dropTarget.current, handleDrop]);


    useEffect(() => {
        if (mode === EditTagMode.ADD) {
            getProtectedResource(`/tag2.php?task=tag-settings&pid=${projectId}`)
                .then(response => {

                    const { categories, disciplines, statuses } = response.data.settings;

                    setTagSettings(old => ({
                        ...old,
                        categories:categories,
                        disciplines:disciplines,
                        statuses:statuses,
                    }));

                    let cat = tagData.category;
                    if (cat === -1 && categories.length > 0)
                        cat = categories[0].id;
                    let dis = tagData.discipline;
                    if (dis === -1 && disciplines.length > 0)
                        dis = disciplines[0].id;
                    let stat = tagData.status;
                    if (stat === -1 && statuses.length > 0)
                        stat = statuses[0].id;

                    setTagData(old => ({
                        ...old,
                        category:cat,
                        discipline:dis,
                        status:stat
                    }));
                })
                .catch(() => {
                });
        }
        else {
            Promise
                .all([
                    getProtectedResource(`/tag2.php?task=tag-settings&pid=${projectId}`),
                    getProtectedResource(`/tag2.php?task=tag-state&tid=${tagId}`)
                ])
                .then(([respSettings, respTag]) => {

                    const { categories, disciplines, statuses } = respSettings.data.settings;
                    const { tag } = respTag.data;

                    setTagSettings(old => ({
                        ...old,
                        categories:categories,
                        disciplines:disciplines,
                        statuses:statuses,
                    }));
                    setTagData(old => ({
                        ...old,
                        title:tag.title,
                        tag:tag.tag,
                        category:tag.category_id,
                        discipline:tag.discipline_id,
                        status:tag.status_id,
                    }));
                })
                .catch(() => {
                })
            ;
        }
    }, [mode]);


    const categoryOpts = (tagSettings.categories || []).map(category => (
        <option key={category.id} value={category.id}>{category.category}</option>
    ));

    const disciplineOpts = (tagSettings.disciplines || []).map(discipline => (
        <option key={discipline.id} value={discipline.id}>{discipline.discipline}</option>
    ));

    const statusOpts = (tagSettings.statuses || []).map(status => (
        <option key={status.id} value={status.id}>{status.status}</option>
    ));

    const isEdit = mode === EditTagMode.EDIT;
    let caption = 'Edit tag';
    if (isEdit) {
        caption += `: ${tagData.tag}`;
    }

    console.log(tagData)

    return (
        <Dialog
            title={caption}
            canEscapeKeyClose
            canOutsideClickClose
            isOpen={isOpen}
            onClose={onClose}
            style={{
                minWidth: '10rem',
                minHeight: '10rem',
                zIndex: 150
            }}
        >
            <div style={{width: '100%', height: '100%', padding:'1rem 1rem 0 1rem'}}>
                <FormGroup labelFor={'tag-title'} label={'Title'}>
                    <InputGroup
                        id={'tag-title'}
                        value={tagData.title}
                        disabled={isWorking}
                        onChange={(e) => setTagData(old => ({...old, title:e.target.value}))}
                    />
                </FormGroup>
                <ControlGroup fill>
                    <FormGroup labelFor={'tag-category'} label={'Category'}>
                        <HTMLSelect
                            id={'tag-category'}
                            disabled={isWorking}
                            fill
                            value={tagData.category}
                            onChange={(e) => setTagData(old => ({...old, category:1*e.target.value}))}
                        >
                            {categoryOpts}
                        </HTMLSelect>
                    </FormGroup>
                    <FormGroup labelFor={'tag-discipline'} label={'Discipline'}>
                        <HTMLSelect
                            id={'tag-discipline'}
                            disabled={isWorking}
                            fill
                            value={tagData.discipline}
                            onChange={(e) => setTagData(old => ({...old, discipline:1*e.target.value}))}
                        >
                            {disciplineOpts}
                        </HTMLSelect>
                    </FormGroup>
                </ControlGroup>
                <ControlGroup fill>
                    <FormGroup labelFor={'tag-status'} label={'Status'}>
                        <HTMLSelect
                            id={'tag-status'}
                            disabled={isWorking}
                            fill
                            value={tagData.status}
                            onChange={(e) => setTagData(old => ({...old, status:1*e.target.value}))}
                        >
                            {statusOpts}
                        </HTMLSelect>
                    </FormGroup>
                </ControlGroup>
                <FormGroup labelFor={'tag-desc'} label={'Comment'}>
                    <TextArea
                        id={'tag-desc'}
                        growVertically={false}
                        disabled={isWorking}
                        fill
                        value={tagData.comment}
                        onChange={(e) => setTagData(old => ({...old, comment:e.target.value}))}
                    />
                </FormGroup>
                <FormGroup>
                    <div ref={dropTarget}
                         style={{
                             position:'relative',
                             display: 'flex',
                             flexDirection:'column',
                             border: '2px dashed rgb(196,196,196)',
                             borderRadius:'5px',
                             backgroundColor:'rgb(224,224,224)',
                             backgroundSize:'cover',
                             justifyContent: 'flex-end',
                             alignItems: 'center',
                             gap: '8px',
                             height: '12rem',
                             padding: '8px'
                         }}
                    >
                        <div ref={dropDisplay}
                             style={{
                                 display: 'flex',
                                 justifyContent: 'center',
                                 alignItems: 'center',
                                 width:'100%',
                                 height:'100%',
                                 backgroundSize:'contain',
                                 backgroundRepeat:'no-repeat',
                                 backgroundPosition:'center center',
                                 pointerEvents:'none'
                             }}
                        >
                            <span
                                style={{
                                    textShadow:'0px 0px 8px #FFFFFF',
                                    fontSize:'150%'
                                }}
                            >
                                Drop photo here or
                            </span>
                        </div>
                        <Button text="Browse..."
                                disabled={isWorking}
                                minimal={false}
                                onClick={() => {
                                    const fileInput = document.getElementById('fi');
                                    fileInput.click();
                                }}
                        />
                        <input type={'file'}
                               id={'fi'}
                               accept="image/png,image/jpeg"
                               onChange={e => handleDrop(e.target.files[0])}
                               style={{display:'none', visibility: 'hidden'}}
                        />
                    </div>
                </FormGroup>
                <ControlGroup fill>
                    <div className={Classes.FILL} />
                    <Button text={'OK'}
                            style={{minWidth:'6rem'}}
                            className={Classes.FIXED}
                            loading={isWorking}
                            disabled={tagData.title.trim().length === 0 || isWorking}
                            onClick={isEdit ? onEditTag : onAddTag}
                    />
                    <Button text={'Cancel'}
                            style={{minWidth:'6rem'}}
                            disabled={isWorking}
                            className={Classes.FIXED}
                            onClick={() => onClose(false)}
                    />
                </ControlGroup>
            </div>
        </Dialog>
    );
}